export type HomeTileLink = {
  labelKey: string;
  href: string;
  /** Optional work-report queue key to show a live count. */
  queueKey?: string;
  /** Hide when permission missing. */
  permission?: string;
};

export type HomeTileDef = {
  id: string;
  titleKey: string;
  icon: string;
  accent: string;
  background: string;
  links: HomeTileLink[];
  /** Entire tile hidden without this permission. */
  permission?: string;
};

/**
 * Home desk tiles — concepts with deep links into real filtered views.
 * DECISION: prefer a ~3×3 strain/biolog desk; phage tiles append when `phages.view`
 * is granted so the phage domain stays visible without dropping core strain tiles.
 */
export const HOME_TILES: HomeTileDef[] = [
  {
    id: "strains",
    titleKey: "home.tiles.strains.title",
    icon: "experiment",
    accent: "#0b6e4f",
    background: "#e8f5ef",
    links: [
      { labelKey: "home.tiles.strains.browse", href: "/strains" },
      { labelKey: "home.tiles.strains.ready", href: "/strains?migration_status=ready" },
      {
        labelKey: "home.tiles.strains.notReady",
        href: "/strains?not_ready=true",
        queueKey: "not_ready",
      },
    ],
  },
  {
    id: "phages",
    titleKey: "home.tiles.phages.title",
    icon: "bug",
    accent: "#6d28d9",
    background: "#f3e8ff",
    permission: "phages.view",
    links: [
      { labelKey: "home.tiles.phages.browse", href: "/phages" },
      { labelKey: "home.tiles.phages.create", href: "/phages", permission: "phages.manage" },
      { labelKey: "home.tiles.phages.export", href: "/phages" },
    ],
  },
  {
    id: "attention",
    titleKey: "home.tiles.attention.title",
    icon: "alert",
    accent: "#b45309",
    background: "#fff7ed",
    links: [
      {
        labelKey: "home.tiles.attention.taxonomy",
        href: "/strains?taxonomy_pending=true",
        queueKey: "taxonomy_pending",
      },
      {
        labelKey: "home.tiles.attention.location",
        href: "/strains?location_pending=true",
        queueKey: "location_pending",
      },
      {
        labelKey: "home.tiles.attention.storage",
        href: "/strains?storage_pending=true",
        queueKey: "storage_pending",
      },
    ],
  },
  {
    id: "biolog",
    titleKey: "home.tiles.biolog.title",
    icon: "biolog",
    accent: "#1d4ed8",
    background: "#eff6ff",
    links: [
      { labelKey: "home.tiles.biolog.catalog", href: "/biolog" },
      { labelKey: "home.tiles.biolog.unlinked", href: "/biolog?linked=false" },
      {
        labelKey: "home.tiles.biolog.noPlate",
        href: "/strains?bioenabled=true&has_biolog_plate=false",
        queueKey: "bioenabled_no_plate",
      },
    ],
  },
  {
    id: "biologQuery",
    titleKey: "home.tiles.biologQuery.title",
    icon: "search",
    accent: "#0369a1",
    background: "#e0f2fe",
    links: [
      { labelKey: "home.tiles.biologQuery.open", href: "/biolog/search" },
      { labelKey: "home.tiles.biologQuery.hint", href: "/biolog/search" },
    ],
  },
  {
    id: "cryoboxes",
    titleKey: "home.tiles.cryoboxes.title",
    icon: "inbox",
    accent: "#0f766e",
    background: "#ccfbf1",
    links: [
      { labelKey: "home.tiles.cryoboxes.registry", href: "/cryoboxes" },
      { labelKey: "home.tiles.cryoboxes.hint", href: "/cryoboxes" },
    ],
  },
  {
    id: "dictionaries",
    titleKey: "home.tiles.dictionaries.title",
    icon: "book",
    accent: "#7c2d12",
    background: "#ffedd5",
    links: [
      { labelKey: "home.tiles.dictionaries.open", href: "/dictionaries" },
      { labelKey: "home.tiles.dictionaries.hint", href: "/dictionaries" },
    ],
  },
  {
    id: "reports",
    titleKey: "home.tiles.reports.title",
    icon: "chart",
    accent: "#4338ca",
    background: "#eef2ff",
    links: [
      { labelKey: "home.tiles.reports.overview", href: "/reports?tab=overview" },
      { labelKey: "home.tiles.reports.map", href: "/reports?tab=map" },
      { labelKey: "home.tiles.reports.work", href: "/reports?tab=work" },
    ],
  },
  {
    id: "phageReports",
    titleKey: "home.tiles.phageReports.title",
    icon: "phageChart",
    accent: "#7c3aed",
    background: "#ede9fe",
    permission: "phages.view",
    links: [
      { labelKey: "home.tiles.phageReports.lysing", href: "/phage-reports?tab=lysing" },
      { labelKey: "home.tiles.phageReports.heatmap", href: "/phage-reports?tab=heatmap" },
      { labelKey: "home.tiles.phageReports.catalog", href: "/phages" },
    ],
  },
  {
    id: "migration",
    titleKey: "home.tiles.migration.title",
    icon: "migration",
    accent: "#9f1239",
    background: "#ffe4e6",
    permission: "migration.manage",
    links: [
      {
        labelKey: "home.tiles.migration.center",
        href: "/migration",
        permission: "migration.manage",
      },
      {
        labelKey: "home.tiles.migration.plan",
        href: "/migration-plan",
        permission: "migration.manage",
      },
      {
        labelKey: "home.tiles.migration.storage",
        href: "/strains?storage_pending=true",
        queueKey: "storage_pending",
      },
    ],
  },
  {
    id: "settings",
    titleKey: "home.tiles.settings.title",
    icon: "settings",
    accent: "#334155",
    background: "#f1f5f9",
    links: [
      { labelKey: "home.tiles.settings.profile", href: "/profile" },
      { labelKey: "home.tiles.settings.audit", href: "/audit", permission: "audit.view" },
      { labelKey: "home.tiles.settings.users", href: "/users", permission: "users.manage" },
    ],
  },
];

export type HomeQueueCounts = Record<string, number>;

/** Fill the 9th slot when Migration is hidden so the grid stays 3×3. */
export const HOME_SEQUENCES_TILE: HomeTileDef = {
  id: "sequences",
  titleKey: "home.tiles.sequences.title",
  icon: "dna",
  accent: "#6d28d9",
  background: "#f3e8ff",
  links: [
    {
      labelKey: "home.tiles.sequences.sixteen",
      href: "/strains?sixteen_pending=true",
      queueKey: "sixteen_pending",
    },
    { labelKey: "home.tiles.sequences.has16s", href: "/strains?sixteenenabled=true" },
    {
      labelKey: "home.tiles.sequences.uncertainGenus",
      href: "/strains?taxon_genus_uncertain=true",
      queueKey: "uncertain_genus",
    },
  ],
};

export function resolveHomeTiles(hasPermission: (code: string) => boolean): HomeTileDef[] {
  const tiles = HOME_TILES.filter((tile) => !tile.permission || hasPermission(tile.permission)).map(
    (tile) => ({
      ...tile,
      links: tile.links.filter((link) => !link.permission || hasPermission(link.permission)),
    }),
  );
  const hasMigration = tiles.some((tile) => tile.id === "migration");
  const coreCount = tiles.filter(
    (tile) => tile.id !== "phages" && tile.id !== "phageReports",
  ).length;
  if (coreCount < 9 && !hasMigration && !tiles.some((tile) => tile.id === "sequences")) {
    const settingsIdx = tiles.findIndex((tile) => tile.id === "settings");
    tiles.splice(settingsIdx >= 0 ? settingsIdx : tiles.length, 0, HOME_SEQUENCES_TILE);
  }
  return tiles;
}
